import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, WritableSignal, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { CountryDbModel } from '@models/Country-db.model';
import { INVOICE_ADDRESS_CONSTANTS } from '@constants/invoice-address.constants';
import { InvoiceAddress } from '@models/invoice-address.model';
import { splitStreetAndHouseNumber } from '@services/utils/invoice-address.util';
import { UserInfo } from '@models/user.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioTranslateResolverPipe } from '@pipes/calio-translate-resolver.pipe';
import { CalioTranslationSortPipe } from '@pipes/calio-translation-sort.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { BookingService } from '@services/feature/booking.service';
import { CountryHelperService } from '@services/utils/country-helper.service';
import { CalioTelInputComponent } from '@ui-lib/calio-intl-tel-input/calio-tel-input/calio-tel-input.component';

/**
 * Standalone "Invoice" payment mode: where the invoice goes. Optional (behind a "different
 * address" checkbox) when the booking form already collects an address the backend can
 * snapshot; mandatory — checkbox dropped, form always shown — when it does not.
 */
@Component({
  selector: 'app-invoice-address-form',
  templateUrl: './invoice-address-form.component.html',
  styleUrls: ['./invoice-address-form.component.scss'],
  imports: [FormsModule, NgSelectModule, TranslateModule, CalioTranslationPipe, CalioTranslationSortPipe, CalioMeetingTemplatePipe, CalioTranslateResolverPipe, CalioTelInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceAddressFormComponent implements OnInit {
  /** True when the booking form collects no address (`context.customer` has no `address`):
   * there is nothing for the backend to snapshot, so the address cannot be optional. */
  readonly isAddressMandatory = input(false);

  readonly lang = input('');

  /** The partner's country identifier ("ch", "in", …), used only to preselect the dropdown.
   * The country belongs to the INVOICE address, not the booker. */
  readonly defaultCountryCode = input('');

  /** Snapshot of the booking form's values, COPIED once into empty fields when the form is
   * revealed. A plain data object, never a shared control: the invoice recipient can differ
   * from the booker, so the two forms must not move together. */
  readonly prefill = input<UserInfo | null>(null);

  readonly invoiceAddressChangeEvent = output<{ checked: boolean; valid: boolean; value: InvoiceAddress | null }>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly countryHelperService = inject(CountryHelperService);
  protected readonly bookingService = inject(BookingService);
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  protected readonly countries = signal<CountryDbModel[]>([]);
  protected readonly countryId = signal<number | undefined>(undefined);

  protected readonly checked = signal(false);
  protected readonly companyName = signal('');
  protected readonly prename = signal('');
  protected readonly lastname = signal('');
  protected readonly street = signal('');
  protected readonly zip = signal('');
  protected readonly city = signal('');
  protected readonly email = signal('');
  protected readonly phone = signal('');

  protected readonly touchedFields = signal<Record<string, boolean>>({});

  protected readonly showForm = computed(() => this.isAddressMandatory() || this.checked());

  protected readonly isEmailValid = computed(() => INVOICE_ADDRESS_CONSTANTS.EMAIL_PATTERN.test(this.email().trim()));

  protected readonly valid = computed(() => {
    if (!this.showForm()) {
      return true;
    }
    return (
      this.prename().trim().length > 0 &&
      this.lastname().trim().length > 0 &&
      // the API requires the house number, so a street without one is not submittable
      splitStreetAndHouseNumber(this.street()).street_number.length > 0 &&
      this.zip().trim().length > 0 &&
      this.city().trim().length > 0 &&
      // an invoice address without a country cannot be taxed correctly
      !!this.countryId() &&
      this.isEmailValid()
    );
  });

  /** The customer typed a street but no house number. Gated on the street field's touched flag
   * because there is no separate number input to blur. */
  protected readonly isStreetTouchedAndMissingNumber = computed(() => {
    const street = this.street().trim();

    return !!this.touchedFields()['street']
      && street.length > 0
      && splitStreetAndHouseNumber(street).street_number.length === 0;
  });

  ngOnInit(): void {
    this.loadCountries();

    // A mandatory form is visible from the start, so it is prefilled now. It may still be
    // invalid, and the parent gates the book button on the emitted `valid`, so it has to hear
    // about that before the customer touches anything.
    if (this.isAddressMandatory()) {
      this.applyPrefill();
      this.emitChange();
    }
  }

  onToggleChecked(): void {
    this.checked.update(checked => !checked);

    if (this.checked()) {
      this.applyPrefill();
    } else {
      this.companyName.set('');
      this.prename.set('');
      this.lastname.set('');
      this.street.set('');
      this.zip.set('');
      this.city.set('');
      this.email.set('');
      this.phone.set('');
      this.touchedFields.set({});
      this.resetCountryToDefault();
    }

    this.emitChange();
  }

  markFieldTouched(field: string): void {
    this.touchedFields.update(touched => ({ ...touched, [field]: true }));
  }

  isFieldRequiredAndEmpty(field: string): boolean {
    if (!this.touchedFields()[field]) {
      return false;
    }
    switch (field) {
      case 'prename':
        return this.prename().trim().length === 0;
      case 'lastname':
        return this.lastname().trim().length === 0;
      case 'street':
        return this.street().trim().length === 0;
      case 'zip':
        return this.zip().trim().length === 0;
      case 'city':
        return this.city().trim().length === 0;
      case 'email':
        return this.email().trim().length === 0;
      default:
        return false;
    }
  }

  isEmailInvalidAndFilled(): boolean {
    return this.touchedFields()['email'] && this.email().trim().length > 0 && !this.isEmailValid();
  }

  onFieldChange(): void {
    this.emitChange();
  }

  /** Dial code for the phone field: the invoice country when chosen, else the partner country. */
  protected readonly phoneCountryCode = computed(() => {
    const selected = this.countries().find(country => country.id === this.countryId());

    return (selected?.identifier ?? this.defaultCountryCode()).toLowerCase();
  });

  protected onPhoneChange(value: string): void {
    this.phone.set(value ?? '');
    this.onFieldChange();
  }
  protected onCountryChange(selection: number | null): void {
    this.countryId.set(selection ?? undefined);
    this.emitChange();
  }

  /** One-time copy of the booking form's values into fields that are still empty. Never
   * overwrites something the customer already typed here. */
  private applyPrefill(): void {
    const source = this.prefill();
    if (!source) {
      return;
    }
    const copy = (target: WritableSignal<string>, value: string | undefined) => {
      if (!target().trim() && value && value.trim()) {
        target.set(value.trim());
      }
    };
    copy(this.companyName, source.company);
    copy(this.prename, source.forename);
    copy(this.lastname, source.lastName);
    copy(this.street, source.street);
    copy(this.zip, source.zip);
    copy(this.city, source.city);
    copy(this.email, source.eMail);
    copy(this.phone, source.phone);
    if (!this.countryId() && source.country) {
      this.countryId.set(source.country);
    }
  }

  /** Clearing the form must leave the country preselected again, not blank — otherwise
   * re-opening it silently blocks the booking on a field the customer never touched. */
  private resetCountryToDefault(): void {
    this.countryId.set(this.findDefaultCountry()?.id);
  }

  private findDefaultCountry(): CountryDbModel | undefined {
    const code = this.defaultCountryCode().toLowerCase();

    return code
      ? this.countries().find(country => country.identifier?.toLowerCase() === code)
      : undefined;
  }

  private loadCountries(): void {
    this.countryHelperService.getCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: countries => {
          if (!countries) {
            return;
          }
          this.countries.set(this.countryHelperService.createNewCountries(countries));

          if (!this.countryId()) {
            const match = this.findDefaultCountry();
            if (match) {
              this.countryId.set(match.id);
              this.emitChange();
            }
          }
        }
      });
  }

  private emitChange(): void {
    const value: InvoiceAddress | null = this.showForm()
      ? {
          company_name: this.companyName().trim() || undefined,
          prename: this.prename().trim(),
          lastname: this.lastname().trim(),
          // the API keeps the two parts separate, so split the one field we collect
          ...splitStreetAndHouseNumber(this.street()),
          zip: this.zip().trim(),
          city: this.city().trim(),
          country_id: this.countryId(),
          email: this.email().trim(),
          phone: this.phone().trim() || undefined,
        }
      : null;

    this.invoiceAddressChangeEvent.emit({
      checked: this.showForm(),
      valid: this.valid(),
      value,
    });
  }
}
